// ─────────────────────────────────────────────────────────────────────────────
// What is this file? Three witnesses, in order of trust: the first bytes, the
// declared MIME type, the file extension. A signature match wins outright
// because it cannot be wrong about what it matched; the other two are what
// someone said, and a `.pdf` that starts with `<html>` is a login page.
// ─────────────────────────────────────────────────────────────────────────────

import type { DetectedFile, FileFamily } from './types.js';

const EXT_FAMILY: Record<string, FileFamily> = {
  pdf: 'pdf',
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', avif: 'image', svg: 'image',
  bmp: 'image', ico: 'image', tif: 'image', tiff: 'image', heic: 'image', heif: 'image', jxl: 'image',
  apng: 'image', nef: 'image', cr2: 'image', arw: 'image', dng: 'image', raf: 'image',
  mp4: 'video', webm: 'video', ogv: 'video', mov: 'video', m4v: 'video', mkv: 'video', avi: 'video',
  wmv: 'video', flv: 'video', mpeg: 'video', mpg: 'video', '3gp': 'video', m3u8: 'video', mpd: 'video',
  mp3: 'audio', wav: 'audio', m4a: 'audio', aac: 'audio', ogg: 'audio', oga: 'audio', opus: 'audio',
  flac: 'audio', aiff: 'audio', aif: 'audio', wma: 'audio', amr: 'audio',
  xlsx: 'spreadsheet', xls: 'spreadsheet', xlsm: 'spreadsheet', ods: 'spreadsheet', csv: 'text', tsv: 'text',
  pptx: 'presentation', ppt: 'presentation', odp: 'presentation', key: 'presentation',
  docx: 'document', doc: 'document', odt: 'document', rtf: 'document', pages: 'document',
  txt: 'text', log: 'text', text: 'text', md: 'text', markdown: 'text', ini: 'text', cfg: 'text', conf: 'text',
  json: 'code', jsonc: 'code', json5: 'code', html: 'code', htm: 'code', xml: 'code', xaml: 'code',
  yaml: 'code', yml: 'code', toml: 'code', sql: 'code', graphql: 'code', gql: 'code', css: 'code', scss: 'code',
  less: 'code', js: 'code', mjs: 'code', cjs: 'code', jsx: 'code', ts: 'code', tsx: 'code', py: 'code', java: 'code',
  cs: 'code', cpp: 'code', cc: 'code', c: 'code', h: 'code', hpp: 'code', go: 'code', rs: 'code', php: 'code',
  rb: 'code', sh: 'code', bash: 'code', zsh: 'code', ps1: 'code', env: 'code', kt: 'code', swift: 'code',
  dart: 'code', lua: 'code', r: 'code', pl: 'code', dockerfile: 'code', makefile: 'code', tf: 'code', proto: 'code',
  zip: 'archive', tar: 'archive', gz: 'archive', tgz: 'archive', bz2: 'archive', '7z': 'archive', rar: 'archive', xz: 'archive'
};

// `.ts` is both TypeScript and an MPEG transport stream. The extension table
// says code; a transport stream announces itself with a 0x47 sync byte every
// 188 bytes, and that signature wins below.

const EXT_LANGUAGE: Record<string, string> = {
  json: 'json', jsonc: 'jsonc', json5: 'json5', html: 'html', htm: 'html', xml: 'xml', xaml: 'xml',
  yaml: 'yaml', yml: 'yaml', toml: 'toml', sql: 'sql', graphql: 'graphql', gql: 'graphql', css: 'css',
  scss: 'scss', less: 'less', js: 'javascript', mjs: 'javascript', cjs: 'javascript', jsx: 'jsx', ts: 'typescript',
  tsx: 'tsx', py: 'python', java: 'java', cs: 'csharp', cpp: 'cpp', cc: 'cpp', c: 'c', h: 'c', hpp: 'cpp', go: 'go',
  rs: 'rust', php: 'php', rb: 'ruby', sh: 'bash', bash: 'bash', zsh: 'bash', ps1: 'powershell', env: 'dotenv',
  kt: 'kotlin', swift: 'swift', dart: 'dart', lua: 'lua', r: 'r', pl: 'perl', dockerfile: 'docker',
  makefile: 'makefile', tf: 'hcl', proto: 'proto', md: 'markdown', markdown: 'markdown', csv: 'csv', tsv: 'tsv',
  txt: 'text', log: 'log', ini: 'ini', cfg: 'ini', conf: 'ini'
};

const MIME_FAMILY: Array<[RegExp, FileFamily]> = [
  [/^application\/pdf$/, 'pdf'],
  [/^image\//, 'image'],
  [/^video\//, 'video'],
  [/^application\/(vnd\.apple\.mpegurl|x-mpegurl|dash\+xml)$/, 'video'],
  [/^audio\//, 'audio'],
  [/spreadsheetml|ms-excel|oasis\.opendocument\.spreadsheet/, 'spreadsheet'],
  [/presentationml|ms-powerpoint|oasis\.opendocument\.presentation/, 'presentation'],
  [/wordprocessingml|msword|oasis\.opendocument\.text|^application\/rtf$|^text\/rtf$/, 'document'],
  [/^application\/(json|ld\+json|xml|x-yaml|yaml|toml|javascript|x-javascript|ecmascript|typescript|x-sh|sql|graphql)$/, 'code'],
  [/^text\/(html|xml|css|javascript|x-python|x-java|x-csharp|x-sql|yaml|x-yaml)$/, 'code'],
  [/^text\//, 'text'],
  [/^application\/(zip|x-zip-compressed|x-tar|gzip|x-gzip|x-7z-compressed|x-rar-compressed|vnd\.rar|x-bzip2|x-xz)$/, 'archive']
];

const MIME_TEXT_CODE = /^text\/(csv|tab-separated-values|plain|markdown)$/;

interface Signature { family: FileFamily; mime: string; test: (b: Uint8Array) => boolean }

const ascii = (b: Uint8Array, at: number, s: string): boolean => {
  if (b.length < at + s.length) return false;
  for (let i = 0; i < s.length; i++) if (b[at + i] !== s.charCodeAt(i)) return false;
  return true;
};
const bytes = (b: Uint8Array, at: number, ...want: number[]): boolean => {
  if (b.length < at + want.length) return false;
  for (let i = 0; i < want.length; i++) if (b[at + i] !== want[i]) return false;
  return true;
};

/** Signatures that identify a family on their own. Order matters where prefixes overlap. */
const SIGNATURES: Signature[] = [
  { family: 'pdf', mime: 'application/pdf', test: (b) => ascii(b, 0, '%PDF') },
  { family: 'image', mime: 'image/png', test: (b) => bytes(b, 0, 0x89, 0x50, 0x4e, 0x47) },
  { family: 'image', mime: 'image/jpeg', test: (b) => bytes(b, 0, 0xff, 0xd8, 0xff) },
  { family: 'image', mime: 'image/gif', test: (b) => ascii(b, 0, 'GIF8') },
  { family: 'image', mime: 'image/webp', test: (b) => ascii(b, 0, 'RIFF') && ascii(b, 8, 'WEBP') },
  { family: 'image', mime: 'image/bmp', test: (b) => ascii(b, 0, 'BM') && b.length > 14 },
  { family: 'image', mime: 'image/tiff', test: (b) => bytes(b, 0, 0x49, 0x49, 0x2a, 0x00) || bytes(b, 0, 0x4d, 0x4d, 0x00, 0x2a) },
  { family: 'image', mime: 'image/x-icon', test: (b) => bytes(b, 0, 0x00, 0x00, 0x01, 0x00) },
  { family: 'image', mime: 'image/avif', test: (b) => ascii(b, 4, 'ftyp') && (ascii(b, 8, 'avif') || ascii(b, 8, 'avis')) },
  { family: 'image', mime: 'image/heic', test: (b) => ascii(b, 4, 'ftyp') && (ascii(b, 8, 'heic') || ascii(b, 8, 'heix') || ascii(b, 8, 'mif1')) },
  { family: 'audio', mime: 'audio/mp4', test: (b) => ascii(b, 4, 'ftyp') && ascii(b, 8, 'M4A ') },
  { family: 'video', mime: 'video/quicktime', test: (b) => ascii(b, 4, 'ftyp') && ascii(b, 8, 'qt  ') },
  { family: 'video', mime: 'video/mp4', test: (b) => ascii(b, 4, 'ftyp') },
  { family: 'video', mime: 'video/webm', test: (b) => bytes(b, 0, 0x1a, 0x45, 0xdf, 0xa3) },
  { family: 'video', mime: 'video/x-msvideo', test: (b) => ascii(b, 0, 'RIFF') && ascii(b, 8, 'AVI ') },
  { family: 'video', mime: 'application/vnd.apple.mpegurl', test: (b) => ascii(b, 0, '#EXTM3U') },
  { family: 'video', mime: 'video/mp2t', test: (b) => b.length > 376 && b[0] === 0x47 && b[188] === 0x47 && b[376] === 0x47 },
  { family: 'audio', mime: 'audio/wav', test: (b) => ascii(b, 0, 'RIFF') && ascii(b, 8, 'WAVE') },
  { family: 'audio', mime: 'audio/mpeg', test: (b) => ascii(b, 0, 'ID3') || (bytes(b, 0, 0xff) && b.length > 1 && (b[1] & 0xe0) === 0xe0 && (b[1] & 0x06) !== 0) },
  { family: 'audio', mime: 'audio/flac', test: (b) => ascii(b, 0, 'fLaC') },
  { family: 'audio', mime: 'audio/ogg', test: (b) => ascii(b, 0, 'OggS') },
  { family: 'audio', mime: 'audio/aiff', test: (b) => ascii(b, 0, 'FORM') && ascii(b, 8, 'AIFF') },
  { family: 'document', mime: 'application/rtf', test: (b) => ascii(b, 0, '{\\rtf') },
  { family: 'archive', mime: 'application/gzip', test: (b) => bytes(b, 0, 0x1f, 0x8b) },
  { family: 'archive', mime: 'application/x-7z-compressed', test: (b) => bytes(b, 0, 0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c) },
  { family: 'archive', mime: 'application/x-rar-compressed', test: (b) => ascii(b, 0, 'Rar!') },
  { family: 'archive', mime: 'application/x-bzip2', test: (b) => ascii(b, 0, 'BZh') },
  { family: 'archive', mime: 'application/x-xz', test: (b) => bytes(b, 0, 0xfd, 0x37, 0x7a, 0x58, 0x5a, 0x00) }
];

/** ZIP and the legacy OLE container hold several families; the extension says which. */
function containerFamily(b: Uint8Array, ext: string): { family: FileFamily; mime: string } | null {
  const zip = ascii(b, 0, 'PK') && b.length > 3 && (b[2] === 3 || b[2] === 5 || b[2] === 7);
  const ole = bytes(b, 0, 0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1);
  if (!zip && !ole) return null;
  const office: Record<string, [FileFamily, string]> = {
    xlsx: ['spreadsheet', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    xlsm: ['spreadsheet', 'application/vnd.ms-excel.sheet.macroEnabled.12'],
    xls: ['spreadsheet', 'application/vnd.ms-excel'],
    ods: ['spreadsheet', 'application/vnd.oasis.opendocument.spreadsheet'],
    pptx: ['presentation', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    ppt: ['presentation', 'application/vnd.ms-powerpoint'],
    odp: ['presentation', 'application/vnd.oasis.opendocument.presentation'],
    key: ['presentation', 'application/vnd.apple.keynote'],
    docx: ['document', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    doc: ['document', 'application/msword'],
    odt: ['document', 'application/vnd.oasis.opendocument.text'],
    pages: ['document', 'application/vnd.apple.pages']
  };
  const hit = office[ext];
  if (hit) return { family: hit[0], mime: hit[1] };
  return zip ? { family: 'archive', mime: 'application/zip' } : { family: 'unknown', mime: 'application/x-ole-storage' };
}

/**
 * Does this look like text? No NUL bytes and almost nothing outside printable
 * ASCII, tabs, newlines and valid UTF-8 continuation ranges in the sample.
 */
export function looksLikeText(b: Uint8Array): boolean {
  if (b.length === 0) return true;
  const n = Math.min(b.length, 8192);
  // A UTF-16 BOM is text too, even though every other byte is NUL.
  if ((b[0] === 0xff && b[1] === 0xfe) || (b[0] === 0xfe && b[1] === 0xff)) return true;
  let suspicious = 0;
  for (let i = 0; i < n; i++) {
    const c = b[i];
    if (c === 0) return false;
    if (c < 7 || (c > 13 && c < 32 && c !== 27)) suspicious++;
  }
  return suspicious / n < 0.02;
}

/** The first bytes of a text file that is really markup or SVG. */
function textMarkup(b: Uint8Array): { family: FileFamily; mime: string; language: string } | null {
  const sample = new TextDecoder('utf-8', { fatal: false }).decode(b.subarray(0, 512)).replace(/^﻿/, '').trimStart().toLowerCase();
  if (sample.startsWith('<svg') || (sample.startsWith('<?xml') && sample.includes('<svg'))) return { family: 'image', mime: 'image/svg+xml', language: 'xml' };
  if (sample.startsWith('<!doctype html') || sample.startsWith('<html')) return { family: 'code', mime: 'text/html', language: 'html' };
  if (sample.startsWith('<?xml')) return { family: 'code', mime: 'application/xml', language: 'xml' };
  return null;
}

export function extensionOf(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) {
    const lower = base.toLowerCase();
    return lower === 'dockerfile' || lower === 'makefile' ? lower : '';
  }
  return base.slice(dot + 1).toLowerCase();
}

export function familyFromMime(mime: string): FileFamily | null {
  const m = mime.toLowerCase().split(';')[0].trim();
  if (!m || m === 'application/octet-stream' || m === 'binary/octet-stream') return null;
  for (const [re, family] of MIME_FAMILY) if (re.test(m)) return family;
  return null;
}

export interface DetectInput {
  name: string;
  mime?: string | null;
  size: number;
  /** The first bytes. Empty when nothing has been read yet (a streamed URL). */
  head: Uint8Array;
}

export function detect(input: DetectInput): DetectedFile {
  const name = input.name || '';
  const extension = extensionOf(name);
  const declaredMime = (input.mime ?? '').toLowerCase().split(';')[0].trim();
  const declaredFamily = familyFromMime(declaredMime) ?? EXT_FAMILY[extension] ?? null;
  const head = input.head;

  let sniffedFamily: FileFamily | undefined;
  let sniffedMime: string | undefined;
  let language: string | undefined = EXT_LANGUAGE[extension];
  let isText = false;

  if (head.length > 0) {
    const sig = SIGNATURES.find((s) => s.test(head));
    if (sig) {
      sniffedFamily = sig.family;
      sniffedMime = sig.mime;
    } else {
      const container = containerFamily(head, extension);
      if (container) {
        sniffedFamily = container.family;
        sniffedMime = container.mime;
      } else if (looksLikeText(head)) {
        isText = true;
        const markup = textMarkup(head);
        if (markup) {
          sniffedFamily = markup.family;
          sniffedMime = markup.mime;
          language = language ?? markup.language;
        }
      }
    }
  }

  let family: FileFamily;
  let mime: string;
  if (sniffedFamily) {
    family = sniffedFamily;
    mime = sniffedMime ?? declaredMime;
  } else if (isText) {
    // Text by inspection: the extension decides between code and plain text.
    family = declaredFamily === 'code' || declaredFamily === 'text' ? declaredFamily
      : EXT_LANGUAGE[extension] && !['text', 'log', 'csv', 'tsv'].includes(extension) ? 'code'
      : 'text';
    mime = declaredMime || (family === 'code' ? 'text/plain' : 'text/plain');
  } else if (declaredFamily) {
    family = declaredFamily;
    mime = declaredMime || guessMimeFromExtension(extension);
  } else {
    family = head.length > 0 ? 'unknown' : 'unknown';
    mime = declaredMime || 'application/octet-stream';
  }

  // Text and code are one surface with two names; a mismatch between them is not one.
  const textish = (f: FileFamily | null) => f === 'text' || f === 'code';
  const mismatch = Boolean(
    sniffedFamily && declaredFamily && sniffedFamily !== declaredFamily && !(textish(sniffedFamily) && textish(declaredFamily))
  ) || Boolean(!sniffedFamily && isText && declaredFamily && !textish(declaredFamily) && declaredFamily !== 'image');

  if (family === 'code' && !language) language = 'text';

  return { family, name, extension, mime, size: input.size, language, sniffedFamily, sniffedMime, mismatch, isText: isText || textish(family) };
}

const EXT_MIME: Record<string, string> = {
  pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
  svg: 'image/svg+xml', avif: 'image/avif', mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', m3u8: 'application/vnd.apple.mpegurl',
  mpd: 'application/dash+xml', mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', ogg: 'audio/ogg', flac: 'audio/flac', opus: 'audio/opus',
  csv: 'text/csv', tsv: 'text/tab-separated-values', txt: 'text/plain', md: 'text/markdown', json: 'application/json', xml: 'application/xml',
  html: 'text/html', htm: 'text/html', css: 'text/css', js: 'text/javascript', ts: 'text/plain', yaml: 'application/x-yaml', yml: 'application/x-yaml',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', xls: 'application/vnd.ms-excel',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', doc: 'application/msword',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', ppt: 'application/vnd.ms-powerpoint',
  zip: 'application/zip', gz: 'application/gzip', rar: 'application/x-rar-compressed', '7z': 'application/x-7z-compressed'
};

export function guessMimeFromExtension(ext: string): string {
  return EXT_MIME[ext] ?? 'application/octet-stream';
}

/** Family from name and declared type only, for deciding whether to stream before any byte arrives. */
export function familyFromMeta(name: string, mime?: string | null): FileFamily {
  return familyFromMime(mime ?? '') ?? EXT_FAMILY[extensionOf(name)] ?? 'unknown';
}

export function isHlsManifest(d: { extension: string; mime: string }): boolean {
  return d.extension === 'm3u8' || /mpegurl/.test(d.mime);
}

export function isDashManifest(d: { extension: string; mime: string }): boolean {
  return d.extension === 'mpd' || /dash\+xml/.test(d.mime);
}
